/**
 * Card Component - Template
 * ==========================
 *
 * Template HTML del componente Card.
 * Separar el template permite:
 * - Mejor legibilidad
 * - Posibilidad de tener templates alternativos
 * - Facilita testing del HTML
 * - Permite hot-reload del template en desarrollo
 */

/**
 * Template principal del Card
 *
 * CONVENCIONES:
 * - Usar $ctrl para acceder al controlador
 * - Usar ng-class para clases dinámicas
 * - Usar ng-if para renderizado condicional (no ng-show)
 * - Incluir atributos ARIA para accesibilidad
 */
export const CARD_TEMPLATE: string = `
<div
  class="ec-overflow-hidden"
  ng-class="$ctrl.getContainerClasses()"
  ng-click="$ctrl.handleClick($event)"
  ng-keydown="$ctrl.handleKeydown($event)"
  role="article"
  tabindex="0"
  ng-attr-aria-disabled="{{ $ctrl.disabled }}"
  ng-attr-aria-label="{{ $ctrl.title }}"
>
  <!-- ======================================================================
       MODO COMPOUND (composición vía ecCardMedia/ecCardContent/etc.)
       Transclusión cruda en la raíz, sin bindings de contenido.
       ====================================================================== -->
  <div ng-if="$ctrl.isCompound" ng-transclude></div>

  <!-- ======================================================================
       LAYOUT STACK (default · comportamiento histórico)
       ====================================================================== -->
  <div ng-if="$ctrl.layout === 'stack' && !$ctrl.isCompound">
    <!-- ===== IMAGEN (full-bleed: toca top y laterales; el card recorta las esquinas con overflow-hidden + rounded-2xl) ===== -->
    <div ng-if="$ctrl.imageUrl" class="ec-relative">
      <img
        ng-src="{{ $ctrl.imageUrl }}"
        alt="{{ $ctrl.imageAlt || $ctrl.title }}"
        class="ec-block ec-w-full ec-h-[120px] ec-object-cover"
      />
      <!-- Badge sobre imagen -->
      <span
        ng-if="$ctrl.badge"
        class="ec-absolute ec-top-2 ec-right-2 ec-px-2 ec-py-1 ec-rounded-full ec-text-xs ec-font-medium"
        ng-class="$ctrl.getVariantClasses().badge"
      >
        {{ $ctrl.badge }}
      </span>
    </div>

    <!-- ===== BLOQUE DE CONTENIDO (padding propio: pt 12px si hay imagen arriba, 16px si no) ===== -->
    <div
      ng-if="$ctrl.hasStackContent"
      class="ec-flex ec-flex-col ec-gap-3"
      ng-class="$ctrl.getStackContentClasses()"
    >
      <!-- ===== HEADER DEL CARD (título + subtítulo agrupados) ===== -->
      <div ng-if="$ctrl.title || $ctrl.subtitle || ($ctrl.badge && !$ctrl.imageUrl)" class="ec-flex ec-items-start ec-justify-between ec-gap-3">
        <div class="ec-flex-1 ec-min-w-0">
          <!-- Título -->
          <h3
            ng-if="$ctrl.title"
            class="ec-font-bold"
            ng-class="$ctrl.getTitleClasses()"
          >
            {{ $ctrl.title }}
          </h3>

          <!-- Subtítulo -->
          <p
            ng-if="$ctrl.subtitle"
            class="ec-text-color-text-muted ec-mt-1"
            ng-class="$ctrl.getSizeClasses().subtitle"
          >
            {{ $ctrl.subtitle }}
          </p>
        </div>

        <!-- Badge (sin imagen) -->
        <span
          ng-if="$ctrl.badge && !$ctrl.imageUrl"
          class="ec-flex-shrink-0 ec-px-2 ec-py-1 ec-rounded-full ec-text-xs ec-font-medium"
          ng-class="$ctrl.getVariantClasses().badge"
        >
          {{ $ctrl.badge }}
        </span>
      </div>

      <!-- ===== CUERPO ===== -->
      <div
        ng-if="$ctrl.description || $ctrl.caption || $ctrl.hasTransclude"
        ng-class="$ctrl.getBodyClasses()"
      >
        <p ng-if="$ctrl.description">{{ $ctrl.description }}</p>
        <p ng-if="$ctrl.caption" class="ec-text-sm ec-text-color-text-muted ec-mt-1">{{ $ctrl.caption }}</p>
        <div ng-transclude ng-if="$ctrl.hasTransclude"></div>
      </div>

      <!-- ===== FOOTER CON ACCIONES ===== -->
    <div
      ng-if="$ctrl.showActions"
      class="ec-flex ec-items-center ec-justify-end ec-gap-2 ec-pt-4 ec-border-t"
      ng-class="$ctrl.getFooterClasses()"
    >
      <button
        ng-if="$ctrl.secondaryAction"
        type="button"
        class="ec-px-3 ec-py-2 ec-text-sm ec-font-medium ec-rounded-lg ec-transition-colors"
        ng-class="$ctrl.getSecondaryButtonClasses()"
        ng-click="$ctrl.handleSecondaryAction($event)"
        ng-disabled="$ctrl.disabled"
      >
        {{ $ctrl.secondaryActionText || 'Cancelar' }}
      </button>
      <button
        ng-if="$ctrl.primaryAction"
        type="button"
        class="ec-px-3 ec-py-2 ec-text-sm ec-font-medium ec-rounded-lg ec-transition-colors"
        ng-class="$ctrl.getPrimaryButtonClasses()"
        ng-click="$ctrl.handlePrimaryAction($event)"
        ng-disabled="$ctrl.disabled"
      >
        {{ $ctrl.primaryActionText || 'Aceptar' }}
      </button>
      </div>
    </div>
  </div>

  <!-- ======================================================================
       LAYOUT SPORT-ROW (avatar + nombre + chip Prime) · Figma
       ====================================================================== -->
  <div ng-if="$ctrl.layout === 'sport-row'" class="ec-flex ec-items-center ec-gap-3">
    <div class="ec-flex-shrink-0 ec-w-12 ec-h-12" ng-class="$ctrl.avatarClass">
      <img
        ng-if="$ctrl.avatar"
        ng-src="{{ $ctrl.avatar }}"
        alt="{{ $ctrl.imageAlt || $ctrl.title }}"
        class="ec-w-full ec-h-full ec-object-cover"
      />
    </div>
    <div class="ec-flex-1 ec-min-w-0">
      <p class="ec-font-medium ec-text-color-text ec-truncate">{{ $ctrl.title }}</p>
      <span ng-if="$ctrl.prime" class="ec-mt-1" ng-class="$ctrl.primeChipClass">
        Precio exclusivo Prime
      </span>
      <p ng-if="$ctrl.caption && !$ctrl.prime" class="ec-text-sm ec-text-color-text-muted ec-truncate ec-mt-1">
        {{ $ctrl.caption }}
      </p>
    </div>
  </div>

  <!-- ======================================================================
       LAYOUT SPORT-TILE (avatar + nombre + caption truncado) · Figma
       ====================================================================== -->
  <div ng-if="$ctrl.layout === 'sport-tile'" class="ec-flex ec-flex-col ec-gap-2">
    <div class="ec-w-14 ec-h-14 ec-mx-auto" ng-class="$ctrl.avatarClass">
      <img
        ng-if="$ctrl.avatar"
        ng-src="{{ $ctrl.avatar }}"
        alt="{{ $ctrl.imageAlt || $ctrl.title }}"
        class="ec-w-full ec-h-full ec-object-cover"
      />
    </div>
    <div class="ec-min-w-0">
      <p class="ec-text-sm ec-font-medium ec-text-color-text ec-line-clamp-2">{{ $ctrl.title }}</p>
      <p ng-if="$ctrl.caption" class="ec-text-xs ec-text-color-text-muted ec-line-clamp-2 ec-mt-1">{{ $ctrl.caption }}</p>
      <span ng-if="$ctrl.prime" class="ec-mt-1" ng-class="$ctrl.primeChipClass">
        Precio exclusivo Prime
      </span>
    </div>
  </div>
</div>
`;

// ============================================================================
// TEMPLATES DE HIJOS (Compound Components)
// ============================================================================

/**
 * ecCardMedia — imagen full-bleed del modo compound
 */
export const CARD_MEDIA_TEMPLATE = `
<img ng-src="{{ $ctrl.src }}" alt="{{ $ctrl.alt || '' }}"
  class="ec-block ec-w-full ec-h-[120px] ec-object-cover" />`;

/**
 * ecCardContent — bloque de contenido del modo compound
 * Padding reducido (pt-3) si el padre tiene ecCardMedia, completo si no.
 */
export const CARD_CONTENT_TEMPLATE = `
<div class="ec-flex ec-flex-col ec-gap-3" ng-class="$ctrl.getClasses()" ng-transclude></div>`;

/**
 * ecCardTitle — título del modo compound
 */
export const CARD_TITLE_TEMPLATE = `<h3 class="ec-text-base ec-font-bold ec-text-color-text" ng-transclude></h3>`;

/**
 * ecCardSubtitle — subtítulo del modo compound
 */
export const CARD_SUBTITLE_TEMPLATE = `<p class="ec-text-base ec-text-color-text-muted" ng-transclude></p>`;

/**
 * ecCardText — texto/descripción del modo compound
 */
export const CARD_TEXT_TEMPLATE = `<p class="ec-text-base ec-text-color-text" ng-transclude></p>`;

/**
 * ecCardCaption — caption del modo compound
 */
export const CARD_CAPTION_TEMPLATE = `<p class="ec-text-sm ec-text-color-text-muted" ng-transclude></p>`;

/**
 * Template compacto para listas
 * Alternativa más simple para usar en listados
 */
export const CARD_COMPACT_TEMPLATE: string = `
<div
  class="ec-flex ec-items-center ec-gap-3 ec-p-3 ec-rounded-lg ec-transition-card ec-cursor-pointer"
  ng-class="$ctrl.getContainerClasses()"
  ng-click="$ctrl.handleClick($event)"
>
  <div ng-if="$ctrl.imageUrl" class="ec-flex-shrink-0">
    <img
      ng-src="{{ $ctrl.imageUrl }}"
      alt="{{ $ctrl.imageAlt || $ctrl.title }}"
      class="ec-w-12 ec-h-12 ec-rounded-lg ec-object-cover"
    />
  </div>
  <div class="ec-flex-1 ec-min-w-0">
    <h4 class="ec-font-medium ec-truncate" ng-class="$ctrl.getVariantClasses().header">
      {{ $ctrl.title }}
    </h4>
    <p ng-if="$ctrl.subtitle" class="ec-text-sm ec-text-gray-500 ec-truncate">
      {{ $ctrl.subtitle }}
    </p>
  </div>
  <span
    ng-if="$ctrl.badge"
    class="ec-flex-shrink-0 ec-px-2 ec-py-1 ec-rounded-full ec-text-xs ec-font-medium"
    ng-class="$ctrl.getVariantClasses().badge"
  >
    {{ $ctrl.badge }}
  </span>
</div>
`;

export default CARD_TEMPLATE;
